import { useEffect, useState } from "react";
import { Loader2, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

const HolidaysAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/holidays");
      setRows(Array.isArray(data) ? data.slice().sort((a, b) => a.date.localeCompare(b.date)) : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    try {
      await staffApi.post("/holidays", form);
      toast.success("Holiday added.");
      setForm({ date: "", name: "" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete holiday "${h.name}" on ${fmtDate(h.date)}? This cannot be undone.`)) return;
    setDeleting(h.id);
    try {
      await staffApi.delete(`/holidays/${h.id}`);
      toast.success("Holiday deleted.");
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDeleting(null);
    }
  };

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  return (
    <div data-testid="admin-holidays">
      <h1 className="font-display text-2xl font-bold text-slate-900">Holiday Management</h1>
      <p className="mt-1 text-sm text-slate-500">Add or remove company holidays. Changes apply to all employees.</p>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 p-6" data-testid="holiday-form-card">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Add Holiday</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date</label>
              <input type="date" required data-testid="holiday-date-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Name</label>
              <input type="text" required data-testid="holiday-name-input" placeholder="e.g. Diwali"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="holiday-form-error">{formError}</p>}
            <button type="submit" disabled={busy} data-testid="holiday-add-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Add Holiday
            </button>
          </form>
        </div>
        <div className="lg:col-span-2">
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
          ) : error ? (
            <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="holiday-admin-error">{error}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="holiday-admin-empty">No holidays listed.</p>
          ) : (
            <ul className="space-y-3" data-testid="holiday-admin-list">
              {rows.map((h) => (
                <li key={h.id} className={`bg-white border border-slate-200 p-4 flex items-center justify-between gap-4 ${h.date < todayStr ? "opacity-50" : ""}`} data-testid={`holiday-admin-row-${h.id}`}>
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 shrink-0 bg-[#0A1128] text-white flex items-center justify-center">
                      <CalendarDays size={16} className="text-[#E8574F]" />
                    </span>
                    <div>
                      <p className="font-display text-sm font-bold text-slate-900">{h.name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{fmtDate(h.date)}</p>
                    </div>
                  </div>
                  <button onClick={() => remove(h)} disabled={deleting === h.id} data-testid={`holiday-delete-${h.id}`}
                    className="inline-flex items-center gap-1.5 border border-red-200 text-[#C5221F] hover:bg-red-50 disabled:opacity-50 px-3.5 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
                    {deleting === h.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default HolidaysAdmin;
